import { COLLECTIONS, ENTITIES } from '~/data/maquette'

/** Une ligne d'une collection : comme dans la base de données `db` de la maquette. */
export type Row = { id: string } & Record<string, unknown>
type Db = Record<string, Row[]>

/** Équivalent de `db` dans la maquette : toutes les collections, chargées depuis l'API Java au démarrage. */
export function useDb() {
  return useState<Db>('db', () => ({}))
}

/** Équivalent de `currentEcoleId` dans la maquette. */
export function useCurrentEcoleId() {
  return useState<string | null>('currentEcoleId', () => null)
}

/** Certaines collections (module Sécurité) sont réservées à ADMIN côté backend : un 403 dessus n'est pas une erreur de session, juste une collection vide pour ce rôle. */
export async function loadDb(): Promise<void> {
  const entries = await Promise.all(COLLECTIONS.map(async (name) => {
    try {
      return [name, await $fetch<Row[]>(`/api/${name}`)] as const
    }
    catch (e) {
      await handleAuthError(e)
      return [name, []] as const
    }
  }))
  const db = useDb()
  db.value = Object.fromEntries(entries)
  const allowedIds = useCurrentUser().value?.ecoleIds
  const allowed = allowedIds ? (db.value.ecoles ?? []).filter(e => allowedIds.includes(String(e.id))) : (db.value.ecoles ?? [])
  const saved = localStorage.getItem('currentEcoleId')
  useCurrentEcoleId().value = (saved && allowed.some(e => String(e.id) === saved)) ? saved : (allowed[0]?.id ?? null)
}

export function findById(key: string, id: unknown): Row | undefined {
  return (useDb().value[key] ?? []).find(r => String(r.id) === String(id))
}

const viaInscription = (r: Row): unknown => {
  const i = findById('inscriptions', r.inscriptionId)
  return i ? i.ecoleId : null
}
const viaVersement = (r: Row): unknown => {
  const v = findById('versements', r.versementId)
  if (!v) return null
  const i = findById('inscriptions', v.inscriptionId)
  return i ? i.ecoleId : null
}
const direct = (r: Row): unknown => r.ecoleId

/** `ecoleScope` de la maquette : à quelle école appartient une ligne (les entités absentes de cette liste ne sont pas limitées à une école). */
export const ECOLE_SCOPES: Record<string, (r: Row) => unknown> = {
  cycles: direct,
  offres: direct,
  niveaux: direct,
  niveauOffre: (r) => {
    const n = findById('niveaux', r.niveauId)
    return n ? n.ecoleId : null
  },
  classes: direct,
  inscriptions: direct,
  historiqueStatutInscription: viaInscription,
  tarifications: direct,
  reductions: direct,
  reductionsAppliquees: viaInscription,
  echeanciers: viaInscription,
  lignesEcheance: (r) => {
    const e = findById('echeanciers', r.echeancierId)
    if (!e) return null
    const i = findById('inscriptions', e.inscriptionId)
    return i ? i.ecoleId : null
  },
  versements: viaInscription,
  unitesEnseignement: direct,
  matieres: direct,
  notes: viaInscription,
  bulletins: viaInscription,
  documentsDossier: viaInscription,
  cartesEtudiantes: viaInscription,
  recus: viaVersement,
  imputations: viaVersement,
}

/** `rowFilter` de la maquette : les quatre vues de la collection « personnes ». */
const ROW_FILTERS: Record<string, (r: Row) => boolean> = {
  etudiants: p => p.role === 'ETUDIANT',
  enseignants: p => p.role === 'ENSEIGNANT',
  parents: p => p.role === 'PARENT',
  personnelAdmin: p => p.role === 'PERSONNEL_ADMIN',
}

/** Limite des lignes d'une entité à l'école courante quand elle en dépend (utilisé pour les listes de choix et les filtres). */
export function scopedRows(key: string, rows: Row[]): Row[] {
  const scope = ECOLE_SCOPES[key]
  const ecoleId = useCurrentEcoleId().value
  return scope && ecoleId ? rows.filter(r => scope(r) === ecoleId) : rows
}

/** `entityRows` de la maquette : les lignes d'une entité, limitées à l'école courante quand l'entité en dépend. */
export function entityRows(key: string): Row[] {
  const entity = ENTITIES[key]
  const arr = useDb().value[entity?.storageKey || key] ?? []
  const filter = ROW_FILTERS[key]
  let rows = filter ? arr.filter(filter) : arr
  const scope = ECOLE_SCOPES[key]
  const ecoleId = useCurrentEcoleId().value
  if (scope && ecoleId) rows = rows.filter(r => scope(r) === ecoleId)
  // Un étudiant n'a pas d'ecoleId propre (ce n'est pas un champ de « personne ») : son école vient de son inscription.
  if (key === 'etudiants' && ecoleId) {
    const inscritIds = new Set((useDb().value.inscriptions ?? []).filter(i => i.ecoleId === ecoleId).map(i => i.etudiantId))
    rows = rows.filter(r => inscritIds.has(r.id))
  }
  return rows
}

/**
 * Une tarification est-elle de type « scolarité » ou « inscription », d'après son code/libellé (les deux catégories que
 * le parcours de Caisse traite différemment) ? Le type de frais est fusionné dans la tarification elle-même : plus
 * d'entité séparée, plus d'hypothèse d'ordre d'insertion (id 1 = scolarité, id 2 = inscription, invalide en UUID).
 */
export function isTarifKind(t: Row, kind: 'SCOLARITE' | 'INSCRIPTION'): boolean {
  const pattern = kind === 'SCOLARITE' ? /scol/i : /inscri/i
  return pattern.test(String(t.code ?? '')) || pattern.test(String(t.libelle ?? ''))
}

export function currentAnnee(): Row | undefined {
  const annees = useDb().value.annees ?? []
  return annees.find(a => a.estCourante) || annees[0]
}

export function titleCase(v: unknown): string {
  if (v === undefined || v === null || v === '') return '—'
  return String(v).toLowerCase().split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}

/** Noms d'école : « sigle » + « nom » (données anciennes « SIGLE, Nom » comprises). */
export function ecoleParts(e: Row | undefined): { sigle: string, nom: string } {
  if (!e) return { sigle: '', nom: '' }
  const parts = String(e.libelle || '').split(', ')
  if (e.sigle) return { sigle: String(e.sigle), nom: parts.length > 1 ? parts.slice(1).join(', ') : parts[0]! }
  return parts.length > 1 ? { sigle: parts[0]!, nom: parts.slice(1).join(', ') } : { sigle: '', nom: parts[0]! }
}

export function ecoleLabelText(e: Row | undefined): string {
  const p = ecoleParts(e)
  return p.sigle ? `${p.nom} (${p.sigle})` : p.nom
}

/** Inscriptions en attente de paiement de l'école courante (badge du menu et notifications). */
export function caissePendingCount(): number {
  const ecoleId = useCurrentEcoleId().value
  return (useDb().value.inscriptions ?? []).filter(i => i.statut === 'EN_ATTENTE_PAIEMENT' && i.ecoleId === ecoleId).length
}

/** `computeRelanceRow` de la maquette : ce qu'un étudiant doit à ce jour (null s'il n'y a rien à relancer). */
export function computeRelanceRow(inscription: Row) {
  const db = useDb().value
  const etu = findById('personnes', inscription.etudiantId)
  const classe = inscription.classeId ? findById('classes', inscription.classeId) : null
  if (!etu || !classe) return null
  const echeancier = (db.echeanciers ?? []).find(e => e.inscriptionId === inscription.id)
  if (!echeancier) return null
  const lignes = (db.lignesEcheance ?? []).filter(l => l.echeancierId === echeancier.id)
    .slice().sort((a, b) => Number(a.numeroTranche) - Number(b.numeroTranche))
  const reste = (l: Row) => Math.max(Number(l.montantDu) - Number(l.montantPaye || 0), 0)
  const rappel = lignes.filter(l => l.statut === 'EN_RETARD').reduce((s, l) => s + reste(l), 0)
  const prochaine = lignes.find(l => l.statut === 'ATTENDU' || l.statut === 'PARTIEL')
  if (!prochaine && rappel <= 0) return null
  const montantPeriode = prochaine ? reste(prochaine) : 0
  return { rappel, montantPeriode, total: rappel + montantPeriode }
}

export function overdueStudentsCount(): number {
  const ecoleId = useCurrentEcoleId().value
  return (useDb().value.inscriptions ?? [])
    .filter(i => i.statut === 'VALIDEE' && i.ecoleId === ecoleId)
    .map(computeRelanceRow)
    .filter(r => r && r.rappel > 0).length
}
